import { useState } from "react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { useTableStoreByRoute } from "@/stores/useTableStore";

import {
  DRIVER_FILES_ACTIONS,
  GeneralDialogProps,
  MAX_ALLOWED_COUNTS,
  ROUTES,
  TABLE_NAMES,
  VEHICLE_FILES_ACTIONS,
  useMediaQueries
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import SelectedRowsTag from "../../../../../../shared/components/tags/SelectedRowsTag";
import BannerMessage from "../../../../../BannerMessage/BannerMessage";
import { BANNER_MESSAGE_STATUSES } from "../../../../../BannerMessage/types";
import Dialog from "../../../../../Dialog/Dialog";
import { useFileDownload } from "../../../../../Table/hooks/useFileDownload";

interface IDownloadDialogProps extends GeneralDialogProps {
  route: ROUTES;
  filesTableName: string;
}

const DownloadDialog = ({
  route,
  isOpen,
  onClose,
  filesTableName
}: IDownloadDialogProps) => {
  const { t } = useTranslation();
  const { downloadFile } = useFileDownload();
  const { toMd } = useMediaQueries();

  const { selectedRows } = useTableStoreByRoute(route);

  const [isLoading, setIsLoading] = useState(false);

  const isreadyToDownload =
    selectedRows.length <= MAX_ALLOWED_COUNTS.DownloadFiles;

  const bannerStatus = isreadyToDownload
    ? BANNER_MESSAGE_STATUSES.Info
    : BANNER_MESSAGE_STATUSES.Warning;

  const resourceAction =
    route === ROUTES.VehicleFiles
      ? VEHICLE_FILES_ACTIONS.Download
      : DRIVER_FILES_ACTIONS.Download;

  const handleCloseDownloadVehicleFilesDialog = () => {
    onClose(resourceAction);
  };

  const generateDownloadUrlQueryParams = () => {
    let firstId: string = "";
    let remainingString: string = "";
    selectedRows.forEach((row: string, index: number) => {
      if (index === 0) {
        firstId = row;
      }
      if (index !== 0) {
        remainingString = remainingString + `&Ids=${row}`;
      }
    });
    return firstId + remainingString;
  };

  const handleSubmitDownload = () => {
    isreadyToDownload &&
      downloadFile({
        queryString: generateDownloadUrlQueryParams(),
        setIsLoading,
        onCloseDialog: handleCloseDownloadVehicleFilesDialog
      });
  };

  return (
    <Dialog
      data-testid={`download-${filesTableName.toLowerCase()}-files-dialog`}
      title={t(`Dialog##download ${filesTableName.toLowerCase()} files`)}
      isTitleSeparator
      open={isOpen}
      close={handleCloseDownloadVehicleFilesDialog}
      submit={handleSubmitDownload}
      extendToMaxWidth={toMd}
      actions={
        <DialogActions
          onClose={handleCloseDownloadVehicleFilesDialog}
          isLoading={isLoading}
          isreadyToConfirm={isreadyToDownload}
          confirmationButtonText={t("Button##download")}
          cancelButtonTestId={`close-${resourceAction}`}
          confirmationButtonTestId={`accept-${resourceAction}`}
          cancelButtonText={t("Button##close")}
          hasTooltip={!isreadyToDownload}
          tooltipText={t("Button##max download files", {
            maxNumber: MAX_ALLOWED_COUNTS.DownloadFiles
          })}
        />
      }
    >
      <BannerMessage
        status={bannerStatus}
        title={t("Dialog##max download files", {
          maxNumber: MAX_ALLOWED_COUNTS.DownloadFiles
        })}
        externalCustomStyle={{ margin: "8px 0 16px 0" }}
      />
      <Typography
        variant="body2Medium"
        sx={{ marginBottom: "16px" }}
        component="div"
      >
        {t("Dialog##download only selection")}
      </Typography>

      <SelectedRowsTag
        resourceName={TABLE_NAMES.Files}
        resourceCount={selectedRows?.length}
      />
    </Dialog>
  );
};

export default DownloadDialog;
